import { ConstraintConfigType } from "types/ConstraintsTypes";
import { InputConstraints } from "../../InputConstraints";
import mainStyles from "style/main.module.scss";
import activityPanelStore from "shared/store/activityPanelStore";
import constraintsStore from "shared/store/constraintsStore";
import { getConstraintField, removeLastFourChars } from "utils/misc";
import { formatItalianNumber } from "utils/formatUtils";
export const GridItem: React.FC<{
  cd: ConstraintConfigType;
  dataKey?: string;
  cv1: string;
  cv2?: string;
  fv: any;
  ohi: any;
  marginTop?: number;
  index?: number;
  notEditableValue?: { value: string; label: string };
  disabled?: boolean;
}> = ({
  cd,
  cv1,
  cv2,
  fv,
  ohi,
  marginTop = 24,
  notEditableValue = null,
  index = -1,
  dataKey,
  disabled = false,
}) => {
  const { ptfThread } = activityPanelStore();
  const predefinitiReference = constraintsStore((s) => s.predefinitiReference);
  const workingFinancialPlan = constraintsStore(
    (s) => s.workingDocument?.risk?.financial_plan as number | undefined,
  );

  const tempFinancialPlanStore: number = workingFinancialPlan ?? 6;

  let gridType = 4;
  let pfValueRender: string = "0";
  switch (dataKey) {
    case "indicator":
      gridType = 2;
      break;
    case "dividend":
    case "cost":
    case "asset":
    case "top_quality":
    case "wrapper":
    case "currency_exposure":
    case "order":
      gridType = 3;
      break;
    case "risk":
      gridType = 5;
      break;
    case "macro_asset_allocation":
    case "macro":
      const field: string = getConstraintField(cv1);
      if (predefinitiReference === null) {
        pfValueRender = "-";
      } else {
        const compositionKey =
          dataKey === "macro"
            ? "composition_macro"
            : "composition_macro_asset_allocation";
        const compositionTable = (predefinitiReference as any)[compositionKey];
        const pfValue = compositionTable?.[tempFinancialPlanStore]?.[field];
        pfValueRender =
          pfValue !== undefined && pfValue !== null
            ? formatItalianNumber(pfValue * 100, 2)
            : "-";
      }

      break;
  }

  //** exceptions. wrong values from BE */
  let valueKey = removeLastFourChars(cv1);
  const hidePtf = dataKey === "order" && !["assets_to_buy", "assets_to_sell"].includes(valueKey);

  let ptfValue = null;

  if (ptfThread?.[dataKey]) {
    ptfValue = ptfThread[dataKey][valueKey];

    // Check if the specific value exists and is valid
    if (ptfValue !== null && ptfValue !== undefined && !isNaN(ptfValue)) {
      if (cd[cv1].type === "percentage") {
        ptfValue = ptfValue * 100;
      }
      const fieldType = cd[cv1]?.type;
      const decimals = fieldType === "int" || fieldType === "integer" ? 0 : 2;
      ptfValue = formatItalianNumber(ptfValue, decimals);
    } else {
      // Value exists in dataKey but specific field is null/undefined/invalid
      ptfValue = "-";
    }
  } else {
    // No portfolio data available for this dataKey
    ptfValue = "-";
  }

  //** exception for risk */
  if (ptfThread && cv1 === "target_var" && dataKey === "risk") {
    ptfValue = formatItalianNumber(ptfThread.risk.target_var * 100, 2);
  }

  if (fv[cv1] === undefined || fv[cv1] === null || fv[cv1] === "") {
    if (cd[cv1].default) {
      if ((fv[cv1] = cd[cv1].default[tempFinancialPlanStore])) {
        fv[cv1] = cd[cv1].default[tempFinancialPlanStore] * 100;
      } else {
        fv[cv1] = "";
      }
    } else {
      fv[cv1] = "";
    }
  }
  if (cv2) {
    if (fv[cv2] === undefined || fv[cv2] === null || fv[cv2] === "") {
      if (cd[cv2].default) {
        if (dataKey === "macro") {
          fv[cv2] = cd[cv2].default[tempFinancialPlanStore] * 100;
        } else {
          fv[cv2] = "";
        }
      } else {
        fv[cv2] = "";
      }
    }
  }

  const disabledRowStyle = disabled ? { pointerEvents: 'none' as const } : {};

  return (
    <>
      {gridType === 2 && (
        <div style={disabledRowStyle}>
          <div style={{ marginBottom: 12, marginTop: index > 0 ? 16 : 0, color: disabled ? '#b0b5bb' : undefined }}>
            {cd[cv1].label}
            {typeof cd[cv1].ge === 'number' && typeof cd[cv1].le === 'number' && ` (${cd[cv1].ge} - ${cd[cv1].le})`}
          </div>
          <div className={mainStyles.gridContainerPFA1x3}>
            {/* GRID ITEM 1 */}
            <div className={mainStyles.gridItem}>
              <div className="flex items-center">
                {!cd[cv1].notWithTopLabel && (
                  <div className={mainStyles.minMax} style={disabled ? { color: '#b0b5bb', zIndex: 1 } : undefined}>
                    Min {cd[cv1].type === "percentage" && <span> %</span>}
                  </div>
                )}

                <InputConstraints
                  configData={cd}
                  formValues={fv}
                  id={cv1}
                  sectionKey={dataKey}
                  onChange={ohi}
                  disabled={disabled}
                />
              </div>
            </div>
            {/* GRID ITEM 2 */}
            <div className={mainStyles.gridItem}>{/* Empty cell */}</div>
            {/* GRID ITEM 3 */}
            <div className={mainStyles.gridItem}>
              <div className={mainStyles.ptf}>
                <div className={mainStyles.ptfTitle}>PTF</div>
                <div className={mainStyles.ptfValue}>{ptfValue}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {gridType === 3 && (
        <>
          <div style={{ marginBottom: 12, marginTop: index > 0 ? 16 : 0 }}>
            {cd[cv1].label}
          </div>
          <div className={hidePtf ? mainStyles.gridContainer1x2 : mainStyles.gridContainer1x4}>
            {/* GRID ITEM 1 */}
            <div className={mainStyles.gridItem}>
              <div className="flex items-center">
                {!cd[cv1].notWithTopLabel && (
                  <div className={mainStyles.minMax}>
                    {cv1.endsWith("_max") && !cv2
                      ? (cv1 === "investment_bonds_max" || cv1 === "investment_funds_max"
                        ? "inferiore a"
                        : "Max")
                      : "Min"}
                  </div>
                )}

                <InputConstraints
                  configData={cd}
                  formValues={fv}
                  id={cv1}
                  sectionKey={dataKey}
                  onChange={ohi}
                  disabled={disabled}
                />
              </div>
            </div>
            {/* GRID ITEM 2 */}
            {cv2 && (
              <div className={mainStyles.gridItem}>
                <div className="flex items-center">
                  {!notEditableValue && (
                    <div className={mainStyles.minMax}>Max</div>
                  )}
                  {notEditableValue && (
                    <div className={mainStyles.minMax}>
                      {notEditableValue.label}
                    </div>
                  )}

                  <InputConstraints
                    configData={cd}
                    formValues={fv}
                    id={cv2}
                    sectionKey={dataKey}
                    onChange={ohi}
                    notEditableValue={
                      notEditableValue ? notEditableValue.value : null
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
            {!cv2 && (
              <div className={mainStyles.gridItem}>{/* Empty cell */}</div>
            )}
            {/* GRID ITEM 3 */}
            {!hidePtf && (
              <div className={mainStyles.gridItem}>{/* Empty cell */}</div>
            )}
            {/* GRID ITEM 4 */}
            {!hidePtf && (
              <div className={mainStyles.gridItem}>
                <div className={mainStyles.ptf}>
                  <div className={mainStyles.ptfTitle}>PTF</div>
                  <div className={mainStyles.ptfValue}>{ptfValue}</div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {gridType === 4 && (
        <>
          <div style={{ marginBottom: 12, marginTop: index > 0 ? 16 : 0 }}>
            {cd[cv1].label}
          </div>
          <div className={mainStyles.gridContainer1x4}>
            {/* GRID ITEM 1 */}
            <div className={mainStyles.gridItem}>
              <div className="flex items-center">
                {!cd[cv1].notWithTopLabel && (
                  <div className={mainStyles.minMax}>Min</div>
                )}

                <InputConstraints
                  configData={cd}
                  formValues={fv}
                  id={cv1}
                  sectionKey={dataKey}
                  onChange={ohi}
                  disabled={disabled}
                />
              </div>
            </div>
            {/* GRID ITEM 2 */}
            {cv2 && (
              <div className={mainStyles.gridItem}>
                <div className="flex items-center">
                  {!notEditableValue && (
                    <div className={mainStyles.minMax}>Max</div>
                  )}
                  {notEditableValue && (
                    <div className={mainStyles.minMax}>
                      {notEditableValue.label}
                    </div>
                  )}

                  <InputConstraints
                    configData={cd}
                    formValues={fv}
                    id={cv2}
                    sectionKey={dataKey}
                    onChange={ohi}
                    notEditableValue={
                      notEditableValue ? notEditableValue.value : null
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
            {!cv2 && (
              <div className={mainStyles.gridItem}>{/* Empty cell */}</div>
            )}
            {/* GRID ITEM 3 */}
            <div className={mainStyles.gridItem}>
              <div className={mainStyles.ptf}>
                <div className={mainStyles.ptfTitle}>PF</div>
                <div className={mainStyles.ptfValue}>{pfValueRender}</div>
              </div>
            </div>
            {/* GRID ITEM 4 */}
            <div className={mainStyles.gridItem}>
              <div className={mainStyles.ptf}>
                <div className={mainStyles.ptfTitle}>PTF</div>
                <div className={mainStyles.ptfValue}>{ptfValue}</div>
              </div>
            </div>
          </div>
        </>
      )}
      {gridType === 5 && (
        <>
          <div style={{ marginBottom: 12, marginTop: index > 0 ? 16 : 0 }}>
            {cd[cv1].label}
            {cd[cv1].notWithTopLabel && cd[cv1].type === "percentage" && (
              <span> %</span>
            )}
          </div>
          <div className={mainStyles.gridContainerRisk1x4}>
            {/* GRID ITEM 1 */}
            <div className={mainStyles.gridItem}>
              <div className="flex items-center">
                {!cd[cv1].notWithTopLabel && (
                  <div className={mainStyles.minMax}>Min</div>
                )}

                <InputConstraints
                  configData={cd}
                  formValues={fv}
                  id={cv1}
                  sectionKey={dataKey}
                  onChange={ohi}
                  disabled={disabled}
                />
              </div>
            </div>
            {/* GRID ITEM 2 */}
            <div className={mainStyles.gridItem}>{/* Empty cell */}</div>
            {/* GRID ITEM 3 */}
            {cv2 && (
              <div className={mainStyles.gridItem}>
                <div className="flex items-center">
                  <div className={mainStyles.gridItem}>
                    <div className={mainStyles.ptf}>
                      <div className={mainStyles.ptfTitle}>VaR max</div>
                      <div className={mainStyles.ptfValue}>
                        {notEditableValue.value}%
                        {/* TODO to be replaced with PTF value */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!cv2 && (
              <div className={mainStyles.gridItem}>{/* Empty cell */}</div>
            )}
            {/* GRID ITEM 4 */}
            <div className={mainStyles.gridItem}>
              <div className={mainStyles.ptf}>
                <div className={mainStyles.ptfTitle}>PTF</div>
                <div className={mainStyles.ptfValue}>{ptfValue}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
